"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { faChevronDown, FontAwesomeIcon } from '@/constants/icons';
import { getBaseImageUrl, getEventsHref, getMerchantHref, getPromotionHref } from '@/constants/hooks';
import { CategoryData, EventResponse, Merchant, Promotion } from '@/services/dataTypes';

interface Props {
  merchantData: Merchant[]; // Kept for interface compatibility but not used in navItems per request
  categories: CategoryData[];
  mer_slug: string;
  mer_slug_type: string;
  cat_slug: string;
  events: EventResponse[];
  promotions: Promotion[];
  promo_slug: string;
  blog_title?: string;
  blog_url?: string;
  companyDomain: string;
}

export default function NavigationPill({
  merchantData,
  categories,
  mer_slug,
  mer_slug_type,
  cat_slug,
  events,
  promotions,
  promo_slug,
  blog_title,
  blog_url,
  companyDomain,
}: Props) {
  const pathname = usePathname() || '';
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const isActive = (item: string) => {
    switch (item) {
      case 'Home':
        return pathname === '/';
      case 'Stores':
        return pathname.startsWith('/all-stores');
      case 'Categories':
        return pathname.includes(`/${cat_slug}`);
      case 'Products':
        return pathname.startsWith('/all-products') || pathname.startsWith('/products');
      case 'Events':
        return pathname.startsWith('/events');
      case 'Promotions':
        return pathname.includes(`/${promo_slug}`);
      default:
        return pathname === blog_url;
    }
  };

  const navItems = [
    { key: 'Home', href: '/', hasDropdown: false },
    ...(merchantData && merchantData.length > 0 ? [{ key: 'Stores', href: '/all-stores/A', hasDropdown: true }] : []),
    ...(categories && categories.length > 0 ? [{ key: 'Categories', href: `/${cat_slug}`, hasDropdown: true }] : []),
    { key: 'Products', href: '/all-products', hasDropdown: true },
    ...(events && events.length > 0 ? [{ key: 'Events', href: '/events', hasDropdown: true }] : []),
    ...(promotions && promotions.length > 0 ? [{ key: 'Promotions', href: `/${promo_slug}`, hasDropdown: true }] : []),
    ...(blog_title && blog_url ? [{ key: blog_title, href: blog_url, hasDropdown: false }] : []),
  ];

  const getDropdownPosition = (itemKey: string, index: number) => {
    // Wide dropdowns (Categories, Stores) should align to the left of their trigger
    // so they expand rightward and don't overflow the viewport on the right.
    // Items near the end of the nav should anchor to the right edge of the trigger.
    const wideDropdowns = ['Categories', 'Stores'];
    const isLastOrNearEnd = index >= navItems.length - 2;

    if (isLastOrNearEnd) return 'right-0';
    if (wideDropdowns.includes(itemKey)) return '-left-20';
    return 'left-1/2 -translate-x-1/2';
  };

  const dropdownVariants: Variants = {
    hidden: { opacity: 0, y: 15, scale: 0.97, filter: 'blur(4px)' },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      filter: 'blur(0px)',
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 30,
        staggerChildren: 0.04,
        delayChildren: 0.02,
      },
    },
    exit: {
      opacity: 0,
      y: 10,
      scale: 0.98,
      filter: 'blur(2px)',
      transition: { duration: 0.15, ease: 'easeOut' },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 },
  };

  const renderDropdown = (itemKey: string) => {
    switch (itemKey) {
      case 'Stores':
        return (
          <div className="grid grid-cols-2 gap-2 w-[420px] max-w-[85vw] p-1">
            {merchantData?.slice(0, 10).map((item, idx) => (
              <motion.div key={idx} variants={itemVariants} whileHover={{ scale: 1.02, x: 2 }}>
                <Link
                  href={getMerchantHref(item, mer_slug, mer_slug_type)}
                  className="flex items-center gap-3 p-2 rounded-lg transition-all duration-300 bg-slate-50 hover:bg-[#5D27A1]/5 border border-slate-100 hover:border-[#5D27A1]/20 group/item"
                >
                  <div className="relative w-8 h-8 rounded-full bg-white border border-slate-200 overflow-hidden shadow-sm flex-shrink-0 flex items-center justify-center p-1">
                    {item?.merchant_logo ? (
                      <Image
                        src={getBaseImageUrl(companyDomain, item?.merchant_logo, '')}
                        alt={item?.merchant_name}
                        fill
                        className="object-contain p-1"
                      />
                    ) : (
                      <span className="text-[10px] font-bold text-slate-400">LOGO</span>
                    )}
                  </div>
                  <span className="text-[13px] font-semibold tracking-wide text-slate-700 group-hover/item:text-[#5D27A1] transition-colors truncate">
                    {item.merchant_name}
                  </span>
                </Link>
              </motion.div>
            ))}
            {merchantData?.length > 10 && (
              <motion.div variants={itemVariants} className="col-span-2 mt-1 pt-2 border-t border-slate-100">
                <Link
                  href="/all-stores/A"
                  className="block text-center py-1.5 text-[11px] font-bold tracking-[0.1em] text-slate-500 hover:text-[#5D27A1] transition-all duration-300 uppercase"
                >
                  View All Brands →
                </Link>
              </motion.div>
            )}
          </div>
        );

      case 'Categories':
        return (
          <div className="grid grid-cols-2 gap-2 w-[420px] max-w-[85vw] p-1">
            {categories?.slice(0, 10).map((item: any, idx: number) => (
              <motion.div key={idx} variants={itemVariants}>
                <Link
                  href={`/${item?.url}`}
                  className="flex items-center gap-4 p-2.5 rounded-xl transition-all duration-300 hover:bg-[#5D27A1]/5 group/item relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#5D27A1]/0 via-[#5D27A1]/10 to-[#5D27A1]/0 translate-x-[-100%] group-hover/item:translate-x-[100%] transition-transform duration-700" />
                  <div className="relative w-10 h-10 rounded-lg bg-[#5D27A1]/5 flex items-center justify-center shadow-sm p-2 group-hover/item:shadow-md group-hover/item:bg-white transition-all duration-300 border border-[#5D27A1]/10 group-hover/item:border-[#5D27A1]/20">
                    <Image
                      src={getBaseImageUrl(companyDomain, item?.category_image, '')}
                      alt={item?.name}
                      width={24}
                      height={24}
                      className="object-contain opacity-70 group-hover/item:opacity-100 transition-all duration-300 group-hover/item:scale-110"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-slate-700 group-hover/item:text-[#5D27A1] transition-colors">
                      {item.name}
                    </span>
                    <span className="text-[10px] font-medium text-slate-400 group-hover/item:text-[#5D27A1]/70">
                      Explore category
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
            <motion.div variants={itemVariants} className="col-span-2 mt-2 pt-3 border-t border-slate-100">
              <Link
                href={`/${cat_slug}`}
                className="flex items-center justify-center gap-2 py-2 text-xs font-bold tracking-widest text-slate-500 hover:text-[#5D27A1] transition-colors duration-300 uppercase group/all"
              >
                View All Categories
                <span className="transform group-hover/all:translate-x-1 transition-transform duration-300">→</span>
              </Link>
            </motion.div>
          </div>
        );

      case 'Products':
        return (
          <div className="w-[320px] max-w-[85vw] p-1">
            {[
              { label: 'Branded Products', href: '/products', },
            ].map((item, idx) => (
              <motion.div key={idx} variants={itemVariants}>
                <Link
                  href={item.href}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#5D27A1]/5 group/item transition-all duration-200"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-slate-700 group-hover/item:text-[#5D27A1] transition-colors">
                      {item.label}
                    </span>
                  </div>
                  <span className="ml-auto text-slate-300 text-sm opacity-0 group-hover/item:opacity-100 group-hover/item:translate-x-0.5 transition-all duration-200">→</span>
                </Link>
              </motion.div>
            ))}


          </div>
        );

      case 'Events':
        return (
          <div className="flex flex-col gap-1 w-[240px] max-w-[85vw] p-1">
            {events?.slice(0, 5).map((item, idx) => (
              <motion.div key={idx} variants={itemVariants}>
                <Link
                  href={getEventsHref(item, 'slug')}
                  className="flex items-center justify-between p-3 rounded-xl transition-all duration-300 hover:bg-[#5D27A1]/5 group/item relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#5D27A1]/0 via-[#5D27A1]/10 to-[#5D27A1]/0 translate-x-[-100%] group-hover/item:translate-x-[100%] transition-transform duration-700" />
                  <div className="flex flex-col relative z-10">
                    <span className="text-sm font-semibold text-slate-700 group-hover/item:text-[#5D27A1] truncate transition-colors">
                      {item.name}
                    </span>
                  </div>
                  <span className="text-[#5D27A1] text-sm opacity-0 group-hover/item:opacity-100 transition-all duration-300 transform -translate-x-2 group-hover/item:translate-x-0">
                    →
                  </span>
                </Link>
              </motion.div>
            ))}
            {events?.length > 5 && (
              <motion.div variants={itemVariants} className="mt-2 pt-3 border-t border-slate-100">
                <Link
                  href="/events"
                  className="flex items-center justify-center gap-2 py-2 text-xs font-bold tracking-widest text-slate-500 hover:text-[#5D27A1] transition-colors duration-300 uppercase group/all"
                >
                  Explore All Events
                  <span className="transform group-hover/all:translate-x-1 transition-transform duration-300">→</span>
                </Link>
              </motion.div>
            )}
          </div>
        );

      case 'Promotions':
        return (
          <div className="flex flex-col gap-1 w-[240px] max-w-[85vw] p-1">
            {promotions?.slice(0, 5).map((item, idx) => (
              <motion.div key={idx} variants={itemVariants}>
                <Link
                  href={getPromotionHref(item, promo_slug)}
                  className="flex items-center justify-between p-3 rounded-xl transition-all duration-300 hover:bg-[#5D27A1]/5 group/item relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#5D27A1]/0 via-[#5D27A1]/10 to-[#5D27A1]/0 translate-x-[-100%] group-hover/item:translate-x-[100%] transition-transform duration-700" />
                  <div className="flex flex-col relative z-10">
                    <span className="text-sm font-semibold text-slate-700 group-hover/item:text-[#5D27A1] truncate transition-colors">
                      {item.name}
                    </span>
                  </div>
                  <span className="text-[#5D27A1] text-sm opacity-0 group-hover/item:opacity-100 transition-all duration-300 transform -translate-x-2 group-hover/item:translate-x-0">
                    →
                  </span>
                </Link>
              </motion.div>
            ))}
            {promotions?.length > 5 && (
              <motion.div variants={itemVariants} className="mt-2 pt-3 border-t border-slate-100">
                <Link
                  href={`/${promo_slug}`}
                  className="flex items-center justify-center gap-2 py-2 text-xs font-bold tracking-widest text-slate-500 hover:text-[#5D27A1] transition-colors duration-300 uppercase group/all"
                >
                  All Promotions
                  <span className="transform group-hover/all:translate-x-1 transition-transform duration-300">→</span>
                </Link>
              </motion.div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <nav className="relative z-50 overflow-visible">
      <div className="flex items-center justify-center gap-1 lg:gap-3 xl:gap-5">
        {navItems.map((item, idx) => {
          const active = isActive(item.key);
          const isHovered = hoveredItem === item.key;

          return (
            <div
              key={item.key}
              className="relative py-2 px-1 overflow-visible"
              onMouseEnter={() => item.hasDropdown && setHoveredItem(item.key)}
              onMouseLeave={() => item.hasDropdown && setHoveredItem(null)}
            >
              <Link
                href={item.href}
                className={`
                  relative flex items-center gap-1.5 px-3 py-2 text-[14px] font-semibold tracking-wide rounded-lg transition-all duration-300
                  ${active
                    ? 'text-[#5D27A1] bg-[#5D27A1]/5'
                    : 'text-slate-600 hover:text-[#5D27A1] hover:bg-[#5D27A1]/5'
                  }
                `}
              >
                {item.key}
                {item.hasDropdown && (
                  <motion.span
                    animate={{ rotate: isHovered ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className={`text-[10px] ml-0.5 ${active ? 'text-[#5D27A1]' : 'text-[#5d27a1]'}`}
                  >
                    <FontAwesomeIcon icon={faChevronDown} className="w-2.5 h-2.5" />
                  </motion.span>
                )}
              </Link>

              {item.hasDropdown && (
                <AnimatePresence>
                  {isHovered && (
                    <motion.div
                      variants={dropdownVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className={`absolute top-[100%] pt-2 z-50 ${getDropdownPosition(item.key, idx)}`}
                      style={{ transformOrigin: idx >= navItems.length - 2 ? 'top right' : 'top left' }}
                    >
                      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-100 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)] overflow-hidden relative">
                        {/* Decorative glow inside dropdown using brand forest green */}
                        <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#5D27A1]/5 rounded-full blur-3xl pointer-events-none opacity-60" />

                        <div className="relative z-10">
                          {renderDropdown(item.key)}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}